import React from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Card, CardBody, Select } from "@windmill/react-ui";
import Title from "components/form/Title";
import LabelArea from "components/form/LabelArea";
import Error from "components/form/Error";
import DrawerButton from "components/form/DrawerButton";
import InputArea from "components/form/InputArea";
import SwitchToggle from "components/form/SwitchToggle";
import ApkUploader from "components/image-uploader/ApkUploader";
import { FiX } from "react-icons/fi";
import useApkSubmit from "hooks/useApkSubmit";

const ApkDrawer = ({ id, closeDrawer }) => {
  const {
    register,
    trigger,
    handleSubmit,
    onSubmit,
    loading,
    errors,
    apkUrl,
    setApkUrl,
    isMandatory,
    setIsMandatory,
    apkType,
    setApkType,
  } = useApkSubmit(id, closeDrawer); // Pass closeDrawer here

  const handleCancelApkFile = () => {
    setApkUrl("");
  };

  return (
    <>
      <div className="w-full relative p-6 border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <Title register={register} title="Edit APK" />
      </div>
      <Scrollbars className="w-full md:w-7/12 lg:w-8/12 xl:w-8/12 relative dark:bg-gray-700 dark:text-gray-200">
        <Card className="flex-grow scrollbar-hide w-full max-h-full" style={{ overflow: "visible" }}>
          <CardBody>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="px-2 flex-grow scrollbar-hide w-full max-h-full pb-20">
                <div className="grid grid-cols-12 gap-5 mb-6 items-center">
                  {/* Version Field */}
                  <div className="col-span-12 sm:col-span-6">
                    <LabelArea required label="Version" />
                    <InputArea
                      trigger={trigger}
                      register={register}
                      label="Version"
                      name="version"
                      type="text"
                      placeholder="e.g., 1.0.2"
                      required
                    />
                    <Error errorName={errors.version} />
                  </div>

                  {/* APK Type Field */}
                  <div className="col-span-12 sm:col-span-6">
                    <LabelArea required label="APK Type" />
                    <Select
                      {...register("type", { required: "APK type is required" })}
                      value={apkType}
                      onChange={(e) => setApkType(e.target.value)}
                      className="border h-10 text-sm focus:outline-none block w-full bg-gray-100 border-transparent focus:bg-white"
                      aria-label="Select APK type"
                    >
                      <option value="" disabled>
                        --Choose--
                      </option>
                      <option value="1">Vendor App</option>
                      <option value="2">Executive App</option>
                      <option value="3">Franchise App</option>
                    </Select>
                    <Error errorName={errors.type} />
                  </div>

                  {/* Mandatory Update Field */}
                  <div className="col-span-12 sm:col-span-6">
                    <LabelArea label="Mandatory Update" />
                    <SwitchToggle
                      handleProcess={setIsMandatory}
                      processOption={isMandatory}
                    />
                    <Error errorName={errors.mandatory} />
                  </div>

                  {/* Release Notes Field */}
                  <div className="col-span-12 sm:col-span-6">
                    <LabelArea required label="Release Notes" />
                    <textarea
                      {...register("release_notes", { required: "Release notes are required" })}
                      rows={4}
                      placeholder="Describe what's new..."
                      className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:text-gray-100"
                    />
                    <Error errorName={errors.release_notes} />
                  </div>

                  {/* APK File Field */}
                  <div className="col-span-12">
                    <LabelArea required label="APK File" />
                    {apkUrl ? (
                      <div className="flex items-center">
                        <a
                          href={`${process.env.REACT_APP_API_IMAGE_URL}${apkUrl}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-500 underline"
                        >
                          View Uploaded File
                        </a>
                        <FiX
                          size={20}
                          className="ml-3 cursor-pointer"
                          onClick={handleCancelApkFile}
                          aria-label="Cancel uploaded APK file"
                        />
                      </div>
                    ) : (
                      <ApkUploader
                        folder="apk"
                        name_merge="app_version"
                        fileUrl={apkUrl}
                        setFileUrl={setApkUrl}
                        onFileUpload={(url) => {
                          setApkUrl(url);
                        }}
                      />
                    )}
                    <Error errorName={errors.apk_file} />
                  </div>
                </div>
              </div>

              <DrawerButton isSubmitting={loading} id={id} title="Edit" />
            </form>
          </CardBody>
        </Card>
      </Scrollbars>
    </>
  );
};

export default ApkDrawer;
